import {
  ColDef,
  GridApi,
  GridReadyEvent,
  RowNode,
} from '@ag-grid-community/core';
import { Button, Heading } from '@assembly-js/design-system';
import { useMediaQuery, useTheme } from '@mui/material';
import clsx from 'clsx';
import { useMemo, useState } from 'react';

import { ErrorMessage } from 'src/components/ErrorMessage';
import { DATE_FORMAT } from 'src/constants';
import AgGrid from 'src/legacy/components/AgGrid';
import { BillingPeriodCellRenderer } from 'src/routes/payments/components/price/BillingPeriodCellRenderer';
import { PriceActionsRenderer } from 'src/routes/payments/components/price/PriceActionRenderer';
import { PriceCellRenderer } from 'src/routes/payments/components/price/PriceCellRenderer';
import { ProductPriceTableRowModal } from 'src/routes/payments/services/types';
import { SubscriptionInterval } from 'src/routes/payments/subscriptions/types/subscriptions';
import { LineItem } from 'src/services/api/invoicesApi';
import {
  CreatePriceInput,
  Price,
  PriceType,
} from 'src/services/api/productsApi';
import { DateUtils } from 'src/utils';

const ONE_TIME = 'oneTime';

export const PriceTable = ({
  productId,
  defaultCurrency,
  mode,
  prices = [],
  isAddingNewPrice = false,
  onAddNewPrice,
  lineItems,
  onSetPriceId,
  isSavePriceLoading,
  onAddPrice,
  handleDeletePrice,
  selectedInterval,
  selectedPriceId,
}: {
  productId: string;
  defaultCurrency: string;
  mode: string;
  prices: Price[];
  isAddingNewPrice: boolean;
  onAddNewPrice: (isAddingNewPrice: boolean) => void;
  onAddPrice: (data: CreatePriceInput) => Promise<Price>;
  isSavePriceLoading: boolean;
  handleDeletePrice: (data: ProductPriceTableRowModal) => void;
  lineItems?: LineItem[];
  onSetPriceId?: (priceId: string) => void;
  selectedInterval?: string;
  selectedPriceId?: string;
}) => {
  const theme = useTheme();
  const [priceError, setPriceError] = useState<string | null>(null);

  const isExtraSmallViewPort = useMediaQuery(theme.breakpoints.down('xs'));

  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  const rowData = useMemo(() => {
    if (!prices) return [];
    const sortedPrices = [...prices].sort((a, b) =>
      b.createdAt.localeCompare(a.createdAt),
    );
    return sortedPrices.map((p) => ({
      id: p.id,
      amount: p.amount,
      currency: p.currency,
      interval: p.interval,
      created: DateUtils.formatDate(p?.createdAt, DATE_FORMAT),
      isUsed: p.isUsed,
      isNew: false,
    }));
  }, [prices]);

  const handleAddPrice = () => {
    if (!gridApi || isAddingNewPrice) return;
    onAddNewPrice(true);
    const isOneTimeMode = mode === 'OneTimeStore' || mode === 'All';
    const newRow = {
      id: null,
      amount: '',
      currency: defaultCurrency,

      ...(selectedInterval
        ? // For subscription users: once a price interval (e.g., monthly) is selected for the first product,
          // all subsequently added prices must use the same interval.
          { interval: selectedInterval }
        : !isOneTimeMode
          ? { interval: SubscriptionInterval.Month }
          : { interval: ONE_TIME }),
      created: '',
      isUsed: false,
      isNew: true,
      type: isOneTimeMode ? PriceType.OneTime : PriceType.Recurring,
    };
    gridApi.applyTransaction({ add: [newRow], addIndex: 0 });
  };

  const handlePriceRemove = (data: ProductPriceTableRowModal) => {
    if (!productId || !data.id) return;
    handleDeletePrice(data);
  };

  const handleSaveNewPrice = async (rowNode: RowNode) => {
    if (!productId) return;
    const { data } = rowNode;
    const isOneTime = data.interval === ONE_TIME || mode === 'OneTimeStore';
    try {
      const response = await onAddPrice({
        productId,
        amount: data.amount.replace(/,/g, ''),
        currency: data.currency,
        type: isOneTime ? PriceType.OneTime : PriceType.Recurring,
        ...(isOneTime ? {} : { interval: data.interval }),
      });

      rowNode.setData({
        ...data,
        id: response.id,
        created: DateUtils.formatDate(new Date(), DATE_FORMAT),
        isNew: false,
      });
      onAddNewPrice(false);
      setPriceError(null);

      // Notify parent about the new price
      if (onSetPriceId) {
        onSetPriceId(response.id);
      }
    } catch (error) {
      onAddNewPrice(false);
    }
  };

  const handleDiscard = (rowNode: RowNode) => {
    if (!gridApi) return;
    gridApi.applyTransaction({ remove: [rowNode.data] });
    onAddNewPrice(false);
    setPriceError(null);
  };

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: 'Price',
        field: 'amount',

        maxWidth: isExtraSmallViewPort ? 80 : 112,
        minWidth: isExtraSmallViewPort ? 80 : 112,
        cellRenderer: 'priceCellRenderer',
        headerClass: 'text-left !pl-2',
        cellClass: '!pl-2 !pr-3',
        suppressSizeToFit: true,
        cellRendererParams: { defaultCurrency, productId, setPriceError },
      },

      {
        headerName: 'Period',
        field: 'interval',
        headerClass: 'text-left !pl-2',
        minWidth: isExtraSmallViewPort ? 160 : 200,
        cellClass: '!px-3',
        cellRenderer: 'billingPeriodCellRenderer',
        cellRendererParams: {
          mode,
          selectedInterval,
          lineItems,
        },
      },

      {
        headerName: 'Actions',
        suppressSizeToFit: true,
        pinned: 'right',
        cellClass: '!flex justify-end items-center !pr-2',
        maxWidth: isExtraSmallViewPort ? 50 : 200,

        cellRenderer: 'priceRenderer',
        cellRendererParams: {
          handleClickRemove: handlePriceRemove,
          handleSave: handleSaveNewPrice,
          handleDiscard: handleDiscard,
          entityName: 'price',
          isDeleteDisabled: (data: ProductPriceTableRowModal) =>
            prices?.length === 1 || !data.id,
          isSavePriceLoading: isSavePriceLoading,
          priceError,
          lineItems,
          mode,
          selectedPriceId,
          onSetPriceId,
          selectedInterval,
        },
      },
    ],
    [
      isExtraSmallViewPort,
      defaultCurrency,
      productId,
      mode,
      handlePriceRemove,
      handleSaveNewPrice,
      handleDiscard,
      isSavePriceLoading,
      priceError,
      prices,
      selectedInterval,
      isAddingNewPrice,
      selectedPriceId,
      lineItems,
    ],
  );

  return (
    <div className="flex flex-col gap-3">
      <div
        className={clsx('flex items-center justify-between', {
          'opacity-25 pointer-events-none': isAddingNewPrice,
        })}
      >
        <Heading size="lg">Pricing</Heading>
        <Button
          variant="secondary"
          label=" Add a price"
          prefixIcon="Plus"
          onClick={handleAddPrice}
          disabled={isAddingNewPrice}
        />
      </div>
      <div className="uniform-table-body-height @container">
        <AgGrid
          rowData={rowData}
          columnDefs={columnDefs}
          onCreateNewRow={(params: GridReadyEvent) => setGridApi(params.api)}
          frameworkComponents={{
            priceRenderer: PriceActionsRenderer,
            priceCellRenderer: PriceCellRenderer, // Use direct reference
            billingPeriodCellRenderer: BillingPeriodCellRenderer,
          }}
        />
      </div>
      {priceError && <ErrorMessage message={priceError} />}
    </div>
  );
};
